export type InviteRoleLike = Readonly<{ role?: string | null }> | string | null | undefined;

const SYSTEM_ROLES = new Set([
    'worker',
    'owner',
    'admin',
    'contributor',
    'contributeur',
    'collaborator',
    'collaborateur',
]);

/** Articles FR/EN, y compris élision collée (l'entrepôt, d'achat). */
function stripLeadingArticle(label: string): string {
    return String(label || '')
        .trim()
        .replace(/^(?:le|la|les|the|a|an)\s+/i, '')
        .replace(/^(?:l'|l'|d'|d')/i, '')
        .trim();
}

/** Clé stable : sans accents, sans articles (y compris l'/d'), minuscule. */
export function normalizeInviteRoleKey(label: string): string {
    let s = String(label || '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    // Articles AVANT nettoyage des apostrophes (sinon "l'entrepot" → "l entrepot")
    s = s.replace(/^(?:le|la|les|the|a|an)\s+/i, '');
    s = s.replace(/^(?:l'|l'|d'|d')/i, '');
    s = s
        .replace(/[«»"'`.,;:!?()[\]{}]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return s;
}

/** Libellé d'affichage propre (sans article, Title Case léger). */
export function formatInviteRoleLabel(label: string): string {
    const cleaned = stripLeadingArticle(label).replace(/\s+/g, ' ').trim();
    if (!cleaned) return '';
    return cleaned
        .split(/([\s\-/]+)/)
        .map((part) => {
            if (/^[\s\-/]+$/.test(part)) return part;
            if (part.toUpperCase() === part && part.length <= 3) return part; // HR, IT…
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        })
        .join('');
}

function isSystemRoleLabel(label: string): boolean {
    const key = normalizeInviteRoleKey(label);
    if (!key) return true;
    if (SYSTEM_ROLES.has(key)) return true;
    // Process owner (FR/EN) — ne doit pas apparaître dans le select d'invitation
    if (
        key.includes('process owner') ||
        key.includes('proprietaire') ||
        key.includes('responsable de processus') ||
        key.includes('responsable processus')
    ) {
        return true;
    }
    return false;
}

/**
 * Deduplicate role options for invite selects.
 * Case/articles-insensitive ("client" ≈ "Client" ≈ "le client").
 */
export function dedupeRoleOptions(roles?: ReadonlyArray<InviteRoleLike> | null): { role: string }[] {
    const seen = new Set<string>();
    const out: { role: string }[] = [];
    for (const item of roles ?? []) {
        const raw = String((typeof item === 'string' ? item : item?.role) ?? '').trim();
        if (!raw || isSystemRoleLabel(raw)) continue;
        const key = normalizeInviteRoleKey(raw);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push({ role: formatInviteRoleLabel(raw) });
    }
    return out;
}

/**
 * Data Collection : dédoublonne sans reformater ni filtrer les rôles configurés.
 * Les libellés de `target_roles` peuvent être des identifiants métier à préserver tels quels.
 */
export function dedupeRoleOptionsPreservingLabels(
    roles?: ReadonlyArray<InviteRoleLike> | null
): { role: string }[] {
    const seen = new Set<string>();
    const out: { role: string }[] = [];
    for (const item of roles ?? []) {
        const raw = String((typeof item === 'string' ? item : item?.role) ?? '').trim();
        if (!raw) continue;
        const key = normalizeInviteRoleKey(raw) || raw.toLocaleLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ role: raw });
    }
    return out;
}

/**
 * Union interviews (invités + pending), rôles mentionnés (owner answers), extras.
 */
export function buildInviteRoleOptions(args: {
    interviews?: ReadonlyArray<InviteRoleLike> | null;
    mentionedRoles?: ReadonlyArray<string> | null;
    extraRoles?: ReadonlyArray<string> | null;
}): { role: string }[] {
    return dedupeRoleOptions([
        ...(args.interviews ?? []),
        ...(args.mentionedRoles ?? []),
        ...(args.extraRoles ?? []),
    ]);
}

/** Suit la chaîne d'alias process.role_aliases jusqu'au libellé courant. */
export function resolveRoleAlias(
    label: string,
    aliases?: Readonly<Record<string, string>> | null
): string {
    const raw = String(label || '').trim();
    if (!raw || !aliases || typeof aliases !== 'object') return raw;

    const byNorm = new Map<string, string>();
    for (const [k, v] of Object.entries(aliases)) {
        const value = String(v || '').trim();
        if (!value) continue;
        const nk = normalizeInviteRoleKey(k) || String(k || '').trim().toLowerCase();
        if (nk) byNorm.set(nk, value);
        const rawKey = String(k || '').trim().toLowerCase();
        if (rawKey) byNorm.set(rawKey, value);
    }

    let current = raw;
    const seen = new Set<string>();
    for (let i = 0; i < 12; i++) {
        const key = normalizeInviteRoleKey(current) || current.toLowerCase();
        if (!key || seen.has(key)) break;
        seen.add(key);
        const next = byNorm.get(key);
        if (!next) break;
        const nextTrim = next.trim();
        if (!nextTrim || normalizeInviteRoleKey(nextTrim) === key) break;
        current = nextTrim;
    }
    return current;
}

/** Applique les alias puis déduplique (clé normalisée). */
export function applyRoleAliasesToList(
    roles: ReadonlyArray<string> | null | undefined,
    aliases?: Readonly<Record<string, string>> | null
): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of roles ?? []) {
        const resolved = resolveRoleAlias(String(item || '').trim(), aliases);
        if (!resolved) continue;
        const key = normalizeInviteRoleKey(resolved);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(resolved);
    }
    return out;
}

/** Filtre les rôles masqués (supprimés) — clé normalisée, après résolution d'alias. */
export function filterHiddenRoles(
    roles: ReadonlyArray<string> | null | undefined,
    hiddenRoles?: ReadonlyArray<string> | null,
    aliases?: Readonly<Record<string, string>> | null
): string[] {
    const hiddenKeys = new Set(
        (hiddenRoles ?? [])
            .map((r) => normalizeInviteRoleKey(String(r || '').trim()))
            .filter(Boolean)
    );
    if (hiddenKeys.size === 0) {
        return applyRoleAliasesToList(roles, aliases);
    }
    return applyRoleAliasesToList(roles, aliases).filter((role) => {
        const key = normalizeInviteRoleKey(role);
        return Boolean(key) && !hiddenKeys.has(key);
    });
}

function pushRole(raw: string | null | undefined, bucket: string[]): void {
    const label = stripLeadingArticle(String(raw || '').trim());
    if (label) bucket.push(label);
}

/** Extrait le rôle depuis une question d'invitation owner (FR/EN) — step 1.5. */
export function extractRoleFromInviteQuestion(questionText?: string | null): string | null {
    const q = String(questionText ?? '').trim();
    if (!q) return null;
    const patterns = [
        /(?:souhaitez-vous|voulez-vous)\s+inviter\s+(?:le|la|les|l['’])?\s*(.+?)\s+à\s+participer/i,
        /inviter\s+(?:le|la|les|l['’])?\s*(.+?)\s+à\s+participer/i,
        /would\s+you\s+like\s+to\s+invite\s+(?:the\s+)?(.+?)\s+to\s+participate/i,
        /invite\s+(?:the\s+)?(.+?)\s+to\s+participate/i,
        /beneficial\s+to\s+invite\s+(?:the\s+|them\s+as\s+)?(.+?)(?:\s+to\s+participate|\s*\?)/i,
    ];
    for (const pattern of patterns) {
        const match = q.match(pattern);
        const role = match?.[1]?.trim().replace(/^[\s"'«]+|[\s"'».,;:!?]+$/g, '');
        if (role) return stripLeadingArticle(role);
    }
    return null;
}

/**
 * Rôle à préremplir dans le modal : champ `invited` prioritaire, sinon extraction du texte question.
 * Évite de garder un rôle refusé plus tôt (ex. client) quand `invited` est vide sur la question courante.
 */
export function resolveInviteRoleFromAnswer(answer?: {
    invited?: string | null;
    question_text?: string | null;
} | null): string {
    const fromField = String(answer?.invited ?? '').trim();
    if (fromField) return formatInviteRoleLabel(fromField) || fromField;
    const fromQuestion = extractRoleFromInviteQuestion(answer?.question_text);
    if (fromQuestion) return formatInviteRoleLabel(fromQuestion) || fromQuestion;
    return '';
}

/**
 * Rôles détectés — champs structurés backend uniquement (plus de parsing texte).
 * - answer.detected_roles : extraction LLM (step 1.1, etc.)
 * - answer.invited : rôle invitation step 1.5
 */
export function extractMentionedRolesFromAnswers(
    answers?: ReadonlyArray<{
        invited?: string | null;
        detected_roles?: string[] | null;
    }> | null
): string[] {
    const raw: string[] = [];

    for (const answer of answers ?? []) {
        if (Array.isArray(answer?.detected_roles)) {
            for (const role of answer.detected_roles) {
                pushRole(role, raw);
            }
        }
        if (typeof answer?.invited === 'string' && answer.invited.trim()) {
            pushRole(answer.invited, raw);
        }
    }

    return dedupeRoleOptions(raw).map((r) => r.role);
}

/** Labels that identify the Process Owner group (case-insensitive). */
export function isProcessOwnerRoleLabel(
    label?: string | null,
    processOwnerDisplayLabel?: string | null
): boolean {
    const key = String(label ?? '')
        .trim()
        .toLowerCase();
    if (!key) return false;
    if (key === 'owner' || key === 'admin' || key === 'process owner') return true;
    const display = String(processOwnerDisplayLabel ?? '')
        .trim()
        .toLowerCase();
    return Boolean(display) && key === display;
}

/**
 * Keep Process Owner role(s) first in a role_order list.
 * Other roles keep their relative order.
 */
export function pinProcessOwnerRolesFirst(
    roles?: ReadonlyArray<InviteRoleLike> | null,
    processOwnerDisplayLabel?: string | null
): string[] {
    const deduped = dedupeRoleOptions(roles).map((x) => x.role);
    const owners: string[] = [];
    const rest: string[] = [];
    for (const role of deduped) {
        if (isProcessOwnerRoleLabel(role, processOwnerDisplayLabel)) owners.push(role);
        else rest.push(role);
    }
    return [...owners, ...rest];
}
